@use "../../docs/inlineReference";

// Prose styles of a compiled MDX article: docs pages and blog posts render Markdown the same way.
// Scoped as `article.article`: the weight the docs page's `.page article …` rules had, so the inline
// code, table and list rules keep winning over component styles of equal specificity.

article.article {
  color: var(--color-foreground);
}

/* One flow controls Markdown and embedded examples without adding their margins together. */
article.article > * {
  margin-block: 0;
}

article.article > * + * {
  margin-top: 2rem;
}

article.article > p + p {
  margin-top: 1.5rem;
}

article.article > :is(h1, h2, h3, h4, h5, h6) + * {
  margin-top: 0.75rem;
}

article.article > h1:first-child {
  max-width: 48rem;
  font-size: clamp(2.25rem, 4vw, 3.25rem);
  line-height: 1.08;
  letter-spacing: -0.055em;
}

article.article > h1:first-child + p {
  margin-top: 1rem;
  color: var(--color-muted);
  font-size: clamp(1.05rem, 1.4vw, 1.2rem);
  line-height: 1.7;
}

article.article > h2 {
  margin-top: clamp(3.5rem, 6vw, 4.5rem);
  padding-top: 0;
}

article.article > h3 {
  margin-top: 3rem;
}

article.article > h4,
article.article > h5,
article.article > h6 {
  margin-top: 2.5rem;
}

article.article > p {
  line-height: 1.65;
  color: color-mix(in srgb, var(--color-foreground) 78%, var(--color-muted));
}

article.article a:not([class]) {
  color: var(--color-primary);
  font-weight: 550;
  text-decoration-line: underline;
  text-decoration-color: color-mix(in srgb, var(--color-primary) 35%, transparent);
  text-decoration-thickness: 1px;
  text-underline-offset: 0.18em;
  transition: color 150ms ease, text-decoration-color 150ms ease;
}

article.article a:not([class]):hover {
  color: var(--color-action-hover);
  text-decoration-color: currentColor;
}

article.article > ul,
article.article > ol {
  padding-inline-start: 1.5rem;
  list-style: disc;
  color: color-mix(in srgb, var(--color-foreground) 82%, var(--color-muted));
}

article.article > ol {
  list-style: decimal;
}

article.article :is(ul, ol) :is(ul, ol) {
  margin-block: 0.4rem;
  padding-inline-start: 1.5rem;
}

article.article :is(ul, ol) ul {
  list-style: disc;
}

article.article :is(ul, ol) ol {
  list-style: decimal;
}

article.article > ul li,
article.article > ol li {
  margin: 0.75rem 0;
  padding-inline-start: 0.25rem;
  line-height: 1.65;
}

article.article > ul li p,
article.article > ol li p {
  line-height: inherit;
}

article.article > ul li::marker,
article.article > ol li::marker {
  color: var(--color-muted-foreground);
}

article.article > blockquote {
  border: 1px solid var(--color-primary-border);
  border-inline-start: 3px solid var(--color-primary);
  border-radius: var(--radius-lg);
  background: var(--color-primary-soft);
  padding: 1rem 1.25rem;
}

article.article > blockquote p {
  margin: 0;
}

article.article code:global(.docs-inline-code):not(pre code) {
  @include inlineReference.reference;
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  // Breaks an identifier only when it can't fit a line. Unlike `anywhere`, this keeps the whole
  // identifier in a table column's minimum width, so a column never splits `personDetection`.
  overflow-wrap: break-word;
  border: 0;
  padding: 0.1em 0.3em;
  background: var(--color-inline-code-background);
  color: var(--color-inline-code-foreground);
}

@media (max-width: 700px) {
  article.article > * + * {
    margin-top: 1.75rem;
  }

  article.article > h3 {
    margin-top: 2.75rem;
  }

  article.article > table {
    display: block;
    overflow-x: auto;
    white-space: normal;

    th,
    td {
      min-width: 12rem;
    }
  }
}

article.article > h2 + time {
  display: block;
  margin-top: 0.375rem;
  color: var(--color-muted);
  font-size: 0.9375rem;
  line-height: 1.5;
}

article.article > hr {
  height: 0;
  margin-top: clamp(3.5rem, 6vw, 4.5rem);
  border: 0;
  border-top: 1px solid var(--color-border);
}

article.article > table[data-docs-endpoint-index] td:first-child {
  width: 45%;
  min-width: 13rem;
}

article.article details {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1rem;
}

article.article summary {
  cursor: pointer;
  font-weight: 600;
}

article.article details[open] > * + * {
  margin-top: 1.25rem;
}

// Only major section boundaries get dividers; subsections and steps stay within their section.
article.article[data-section-dividers] > * + * {
  margin-top: 1.5rem;
}

article.article[data-section-dividers] > p + p {
  margin-top: 1.25rem;
}

article.article[data-section-dividers] > h2 {
  margin-top: 2.5rem;
}

article.article[data-section-dividers] > h2 ~ h2 {
  margin-top: 3.5rem;
  border-top: 1px solid var(--color-border);
  padding-top: 2.5rem;
}

article.article[data-section-dividers] > h3 {
  margin-top: 3rem;
}

article.article[data-section-dividers] > :is(h4, h5, h6) {
  margin-top: 2.5rem;
}

article.article[data-section-dividers] > :is(h1, h2, h3, h4, h5, h6) + * {
  margin-top: 0.75rem;
}

article.article[data-section-dividers] > hr {
  margin-top: 3.5rem;
  margin-bottom: 2.5rem;
}

article.article[data-section-dividers] > :is(h2, h3):first-child,
article.article[data-section-dividers] > :is(h1, h2, h3, hr) + :is(h2, h3) {
  border-top: 0;
  padding-top: 0;
}

article.article[data-section-dividers] > hr + :is(h2, h3) {
  margin-top: 0;
}

/* GFM footnotes: a numbered list under a rule at the end of the article. */
article.article > section[data-footnotes] {
  margin-top: 3rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
  color: var(--color-muted-foreground);
  font-size: 0.875rem;
}

article.article > section[data-footnotes] ol {
  padding-inline-start: 1.25rem;
  list-style: decimal;
}

article.article > section[data-footnotes] li::marker {
  color: var(--color-muted-foreground);
}

article.article sup a[data-footnote-ref] {
  font-size: 0.75em;
  text-decoration: none;
}
